import { Container, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material';
import useTranslate from 'Hooks/useTranslate';
import { InputHTMLAttributes } from 'react';
import { Helmet } from 'react-helmet';
import TestIds from 'Tests/TestIds';
import PaperTitle from 'Theme/components/PaperTitle';
import Skeleton from 'Theme/components/Skeleton';
import TableGroupedHeader, { GroupedColumn, HeaderGroup } from 'Theme/components/TableCollection/TableGroupedHeader';

type TwoFactorField = 'forceTotp' | 'forceEmail';

interface Props {
  loading: boolean;
  domains: IOrganizationDomain[];
  totalCount: number;
  currentPage: number;
  onChangePage: (page: number) => void;
  rowsPerPage: number;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  onHandleToggle: (row: IOrganizationDomain, field: TwoFactorField) => void;
  isGuestUsersSecurityEnabled: boolean;
  // Null until the query has answered : the toggles are not rendered before that.
  guestUsersSecurity: IOrganizationGuestUsersSecurity | null;
  onHandleGuestUsersToggle: (field: TwoFactorField) => void;
  canEnforceTwoFactor: boolean;
}

function OrganizationSecurity({
  loading,
  domains,
  totalCount,
  currentPage,
  onChangePage,
  rowsPerPage,
  onChangeRowsPerPage,
  onHandleToggle,
  isGuestUsersSecurityEnabled,
  guestUsersSecurity,
  onHandleGuestUsersToggle,
  canEnforceTwoFactor
}: Readonly<Props>) {
  const ts = useTranslate();

  const headerGroups: HeaderGroup[] = [
    { id: 'domain', label: ts('page.organizationSecurity.domainColumn'), rowSpan: 2 },
    { id: 'twoFactor', label: ts('page.organizationSecurity.twoFactorColumn'), colSpan: 2, align: 'center' }
  ];

  // A published domain cannot be updated at all, and only a real organization owner may enforce a 2FA.
  const twoFactorTooltip = (isPublished: boolean) => {
    if (isPublished) {
      return ts('page.organizationSecurity.publishedDomainTooltip');
    }
    return canEnforceTwoFactor ? '' : ts('page.organizationSecurity.twoFactorNotAllowedTooltip');
  };

  const createTwoFactorColumn = (field: TwoFactorField, labelKey: string): GroupedColumn<IOrganizationDomain> => ({
    key: field,
    width: 0.2,
    align: 'center',
    renderHead: () => labelKey,
    renderSkeleton: () => <Skeleton variant="rectangular" width="50%" />,
    renderCell: ({ row }) => (
      <Tooltip title={twoFactorTooltip(row.isPublished)} arrow>
        <span>
          <Switch
            checked={row[field]}
            color="primary"
            disabled={row.isPublished || !canEnforceTwoFactor}
            onChange={() => onHandleToggle(row, field)}
          />
        </span>
      </Tooltip>
    )
  });

  const columns: GroupedColumn<IOrganizationDomain>[] = [
    {
      key: 'name',
      width: 0.6,
      renderHead: () => ts('page.organizationSecurity.domainColumn'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="30%" />,
      renderCell: ({ row }) => (
        <Typography variant="body2">{row.name}</Typography>
      )
    },
    createTwoFactorColumn('forceTotp', ts('page.organizationSecurity.totpColumn')),
    createTwoFactorColumn('forceEmail', ts('page.organizationSecurity.emailColumn'))
  ];

  const guestUsersToggle = (security: IOrganizationGuestUsersSecurity, field: TwoFactorField, label: string, testId: string) => (
    <Stack direction="row" alignItems="center">
      <Typography variant="body2">{label}</Typography>
      <Tooltip title={canEnforceTwoFactor ? '' : ts('page.organizationSecurity.twoFactorNotAllowedTooltip')} arrow>
        <span>
          <Switch
            checked={security[field]}
            color="primary"
            disabled={!canEnforceTwoFactor}
            onChange={() => onHandleGuestUsersToggle(field)}
            inputProps={{ 'aria-label': label, 'data-testid': testId } as InputHTMLAttributes<HTMLInputElement>}
          />
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <>
      <Helmet title={ts('page.organizations.title')} />
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" color="primary">
          {ts('page.organizationSecurity.title')}
        </Typography>
        <Paper sx={{ padding: 2 }}>
          <PaperTitle
            title={ts('page.organizationSecurity.domainsTitle')}
            info={ts('page.organizationSecurity.domainsInfo')}
            variant="subtitle1"
          />
          <TableGroupedHeader<IOrganizationDomain>
            loading={loading}
            headerGroups={headerGroups}
            columns={columns}
            rows={domains}
            getRowId={(row) => row.name}
            totalCount={totalCount}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
          />
        </Paper>
        {isGuestUsersSecurityEnabled && (
          <Paper sx={{ padding: 2 }} data-testid={TestIds.pages.organizationSecurity.guestUsersSection}>
            <PaperTitle
              title={ts('page.organizationSecurity.guestUsersTitle')}
              info={ts('page.organizationSecurity.guestUsersInfo')}
              variant="subtitle1"
            />
            {/* A toggle rendered from an unknown state would send a patch computed from the wrong baseline : until the
                query answers, the row shows a skeleton, like the domains table above. */}
            {guestUsersSecurity == null ? (
              <Skeleton variant="rectangular" width="100%" height={38} />
            ) : (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">
                  {ts('page.organizationSecurity.guestUsersRowLabel')}
                </Typography>
                {guestUsersToggle(guestUsersSecurity, 'forceTotp', ts('page.organizationSecurity.totpColumn'), TestIds.pages.organizationSecurity.guestUsersForceTotp)}
                {guestUsersToggle(guestUsersSecurity, 'forceEmail', ts('page.organizationSecurity.emailColumn'), TestIds.pages.organizationSecurity.guestUsersForceEmail)}
              </Stack>
            )}
          </Paper>
        )}
      </Container>
    </>
  );
}

export default OrganizationSecurity;
